import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../lib/api";
import { fmtDateTime, fmtMoney, fmtNumber } from "../../../lib/format";
import { errMsg, pct } from "../../../lib/xutil";
import { hasPerm } from "../../../lib/perm";
import { useApi, useCategories } from "../../customer/hooks";
import { Light, PageHead, Panel, btnGhost, btnPrimary, chip } from "../../../components/common/Page";
import { SkeletonBlock } from "../../../components/common/EmptyState";
import { GREEN, NAVY } from "../../customer/insights/Charts";

const DIMS = ["sales_category", "sales_rep", "exp_department", "exp_category"];
const months = (lang) => Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "el-GR", { month: "short", timeZone: "UTC" }).format(new Date(Date.UTC(2026, i, 15))));

const useKeyName = () => {
  const { t, lang } = useI18n();
  const cats = useCategories();
  return (dim, k) => (dim === "sales_category" ? (cats.data || []).find((c) => c.code === k)?.[`name_${lang}`] || k
    : dim === "exp_department" ? t(`x.fin.depts.${k}`) : dim === "exp_category" ? t(`x.fin.expCats.${k}`) : k);
};

const Compare = ({ year, dim }) => {
  const { t, lang } = useI18n();
  const name = useKeyName();
  const r = useApi("budgetCompare", `/budgets/${year}/compare`, { dimension: dim }, { staleTime: 0 });
  if (!r.data) return <SkeletonBlock className="h-64" />;
  const m = months(lang);
  const chart = r.data.totals.map((c, i) => ({ m: m[i], budget: c.budget, actual: c.actual }));
  const td = "px-2 py-2 text-xs tabular-nums whitespace-nowrap";
  return (
    <div className="space-y-4">
      <Panel title={t("x.fin.budgets.compare")} sub={`${t("x.fin.budgets.pulled", { d: fmtDateTime(r.data.pulled_at) })} · ${t("x.fin.budgets.legend")}`} testId="budget-compare">
        <div className="h-60"><ResponsiveContainer><ComposedChart data={chart}><CartesianGrid vertical={false} stroke="#eef1f4" /><XAxis dataKey="m" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={55} />
          <Tooltip formatter={(v) => (v == null ? "—" : fmtMoney(v))} /><Bar dataKey="budget" name={t("x.fin.budgets.budget")} fill="#cbd5e1" radius={[4, 4, 0, 0]} /><Line dataKey="actual" name={t("x.fin.budgets.actual")} stroke={dim.startsWith("sales") ? GREEN : NAVY} strokeWidth={2.5} dot /></ComposedChart></ResponsiveContainer></div>
      </Panel>
      <div className="-mx-4 overflow-x-auto rounded-2xl bg-white shadow-sm sm:mx-0" data-testid="budget-compare-table">
        <table className="w-full min-w-[900px]">
          <thead><tr className="text-left text-[11px] text-slate-500"><th className={td} />{m.map((x) => <th key={x} className={td}>{x}</th>)}<th className={td}>{t("x.fin.budgets.ytd")}</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {[...r.data.rows, { key: "__total", months: r.data.totals, ytd: r.data.ytd }].map((row) => (
              <tr key={row.key} className={row.key === "__total" ? "bg-canvas font-semibold" : ""} data-testid={`compare-row-${row.key}`}>
                <td className={`${td} max-w-[160px] truncate font-medium text-navy`}>{row.key === "__total" ? t("x.total") : name(dim, row.key)}</td>
                {[...row.months, row.ytd].map((c, i) => (
                  <td key={i} className={td} title={`${t("x.fin.budgets.budget")}: ${fmtMoney(c.budget)}`}>
                    <div className="flex items-center gap-1"><Light status={c.status} /><span className="text-navy">{c.actual == null ? "—" : fmtNumber(c.actual, 0)}</span></div>
                    <div className="text-[10px] text-slate-400">{fmtNumber(c.budget, 0)} · {pct(c.var_pct)}</div>
                  </td>))}
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Editor = ({ year, dim, canEdit }) => {
  const { t, lang } = useI18n();
  const name = useKeyName();
  const r = useApi("budget", `/budgets/${year}`, undefined, { staleTime: 0 });
  const [lines, setLines] = useState(null);
  useEffect(() => { if (r.data) setLines(r.data.lines); }, [r.data]);
  if (!lines) return <SkeletonBlock className="h-64" />;
  const set = (k, i, v) => setLines((L) => ({ ...L, [dim]: { ...L[dim], [k]: L[dim][k].map((x, j) => (j === i ? Number(String(v).replace(",", ".")) || 0 : x)) } }));
  const fill = (k) => setLines((L) => ({ ...L, [dim]: { ...L[dim], [k]: Array(12).fill(L[dim][k][0]) } }));
  const save = async () => { try { await api.put(`/budgets/${year}`, { lines }); toast.success(t("x.saved")); r.refetch(); } catch (e) { toast.error(errMsg(t, e)); } };
  const m = months(lang);
  return (
    <Panel title={t("x.fin.budgets.edit")} action={canEdit && <button type="button" onClick={save} className={btnPrimary} data-testid="budget-save">{t("x.save")}</button>} testId="budget-editor">
      <div className="-mx-4 overflow-x-auto sm:mx-0">
        <table className="w-full min-w-[1000px] text-xs">
          <thead><tr className="text-left text-slate-500"><th className="p-1" />{m.map((x) => <th key={x} className="p-1">{x}</th>)}<th className="p-1">{t("x.fin.budgets.yearBudget")}</th></tr></thead>
          <tbody>{Object.entries(lines[dim]).map(([k, v]) => (
            <tr key={k}>
              <td className="max-w-[150px] truncate p-1 font-medium text-navy">{name(dim, k)}{canEdit && <button type="button" onClick={() => fill(k)} className="ml-1 text-[10px] text-brand underline" title={t("x.fin.budgets.fill")} data-testid={`budget-fill-${k}`}>⇉</button>}</td>
              {v.map((x, i) => <td key={i} className="p-0.5"><input disabled={!canEdit} inputMode="decimal" value={x} onChange={(e) => set(k, i, e.target.value)} className="h-9 w-20 rounded-lg border border-slate-200 px-1.5 text-right tabular-nums text-navy outline-none focus:border-brand disabled:bg-slate-50" data-testid={`budget-${k}-${i}`} /></td>)}
              <td className="p-1 text-right font-semibold tabular-nums text-navy">{fmtNumber(v.reduce((a, b) => a + b, 0), 0)}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export default function Budgets() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [dim, setDim] = useState("sales_category");
  const [view, setView] = useState("compare");
  const [k, setK] = useState(0);
  const refresh = async () => { try { await api.post(`/budgets/${year}/actuals/refresh`); setK((x) => x + 1); toast.success(t("x.saved")); } catch (e) { toast.error(errMsg(t, e)); } };
  const canEdit = hasPerm(user, "budgets:write");
  return (
    <div className="space-y-5" data-testid="budgets-page">
      <PageHead title={t("x.fin.budgets.title")} sub={t("x.fin.budgets.sub")}>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-navy" data-testid="budget-year">{[-1, 0, 1].map((d) => <option key={d} value={new Date().getFullYear() + d}>{new Date().getFullYear() + d}</option>)}</select>
        {canEdit && <button type="button" onClick={refresh} className={btnGhost} data-testid="budget-refresh"><RefreshCw className="h-4 w-4" />{t("x.fin.budgets.refresh")}</button>}
      </PageHead>
      <div className="flex flex-wrap gap-2">{DIMS.map((d) => <button key={d} type="button" className={chip(dim === d)} onClick={() => setDim(d)} data-testid={`budget-dim-${d}`}>{t(`x.fin.budgets.dims.${d}`)}</button>)}</div>
      <div className="flex gap-2">{["compare", "edit"].map((v) => <button key={v} type="button" className={chip(view === v)} onClick={() => setView(v)} data-testid={`budget-view-${v}`}>{t(`x.fin.budgets.${v}`)}</button>)}</div>
      {view === "compare" ? <Compare key={`${year}-${dim}-${k}`} year={year} dim={dim} /> : <Editor key={year} year={year} dim={dim} canEdit={canEdit} />}
    </div>
  );
}
